"use client";

import clsx from "clsx";
import { useMemo, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Icon } from "../icons";
import { Select } from "../Select";
import { LEXICON, LEXICON_TOPICS, type LexiconEntry, type LexiconTopic } from "./entries";
import { LexiconChart } from "./LexiconChart";

type I18n = ReturnType<typeof useI18n>;

/** An entry's texts in the language shown, for the search: name, tag, description, trigger, invalidation, list. */
function textOf({ t }: I18n, e: LexiconEntry): string {
  const key = (k: string) => t(`lex.${e.id}.${k}`);
  const list = Array.from({ length: e.list?.n ?? 0 }, (_, k) => `${e.list?.titled ? key(`list.${k}.title`) : ""} ${key(`list.${k}`)}`);
  const read = Array.from({ length: e.read ?? 0 }, (_, k) => `${key(`read.${k}.title`)} ${key(`read.${k}`)}`);
  return [key("name"), key("tag"), key("desc"), e.conf ? key("conf") : "", e.inv ? key("inv") : "", ...list, ...read].join(" ").toLowerCase();
}

/**
 * The reference (the user's trading cheat sheet): candlestick patterns, market structure, liquidity and SMC,
 * confirmation, Fibonacci and risk, context — each entry lean: its schematic chart, one sentence, the market reaction
 * the rulebooks expect, from when it counts and what makes it invalid. Searchable, by topic.
 */
export function Lexicon() {
  const i18n = useI18n();
  const { t } = i18n;
  const [query, setQuery] = useState("");
  const [topic, setTopic] = useState<LexiconTopic | "">("");
  const texts = useMemo(() => new Map(LEXICON.map((e) => [e.id, textOf(i18n, e)])), [i18n]);
  const q = query.trim().toLowerCase();
  const shown = LEXICON.filter((e) => (!topic || e.cat === topic) && (!q || texts.get(e.id)!.includes(q)));
  const topics = [{ value: "" as const, label: t("lex.allCats") }, ...LEXICON_TOPICS.map((c) => ({ value: c, label: t(`lex.cat.${c}`) }))];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-line bg-surface px-2.5 py-1.5">
          <Icon name="search" className="h-4 w-4 text-muted" />
          <input value={query} onChange={(ev) => setQuery(ev.target.value)} placeholder={t("lex.search")} aria-label={t("lex.search")} className="min-w-0 flex-1 bg-transparent text-[13px] outline-none" />
        </label>
        <Select<LexiconTopic | ""> label={t("lex.allCats")} value={topic} options={topics} onChange={setTopic} className="w-[210px]" />
        {(query || topic) && (
          <button onClick={() => { setQuery(""); setTopic(""); }} className="cursor-pointer rounded-lg px-2.5 py-1.5 text-[13px] text-muted hover:bg-surface-2">
            {t("lex.reset")}
          </button>
        )}
      </div>
      <p className="text-[11px] text-muted">{t("lex.intro.note")}</p>
      {shown.length === 0 && <p className="text-[13px] text-muted">{t("lex.none")}</p>}
      {LEXICON_TOPICS.map((c) => (
        <Topic key={c} topic={c} entries={shown.filter((e) => e.cat === c)} />
      ))}
    </div>
  );
}

/** A topic's entries under its heading with their number (nothing when none is shown). */
function Topic({ topic, entries }: { topic: LexiconTopic; entries: LexiconEntry[] }) {
  const { t } = useI18n();
  if (entries.length === 0) return null;
  return (
    <section className="flex flex-col gap-2.5">
      <h3 className="text-[14px] font-semibold text-ink">
        {t(`lex.cat.${topic}`)} <span className="text-[12px] font-normal text-muted">({entries.length})</span>
      </h3>
      <div className="grid grid-cols-1 gap-3 wide:grid-cols-2 xl:grid-cols-3">
        {entries.map((e) => (
          <Entry key={e.id} e={e} />
        ))}
      </div>
    </section>
  );
}

/** One entry, lean: name and tag, the chart, one sentence, the expected reaction, from when it counts and when not, its list. */
function Entry({ e }: { e: LexiconEntry }) {
  const { t } = useI18n();
  const key = (k: string) => t(`lex.${e.id}.${k}`);
  return (
    <article className={clsx("flex flex-col gap-2 rounded-xl border border-line bg-surface p-3", e.wide && "wide:col-span-2")}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
        <h4 className="text-[13px] font-semibold text-ink">{key("name")}</h4>
        <span className="text-[11px] text-muted">{key("tag")}</span>
      </div>
      <LexiconChart e={e} />
      <p className="text-[12px] text-ink">{key("desc")}</p>
      <Reaction e={e} />
      <ReadList e={e} />
      <EntryList e={e} />
      {e.conf && <Rule tone="good" label={t("lex.confirm")} text={key("conf")} />}
      {e.inv && <Rule tone="bad" label={t("lex.invalid")} text={key("inv")} />}
    </article>
  );
}

/** The scale of the expected reaction: bearish, neutral, bullish — the expected part(s) lit. */
const SCALE = [
  { dir: -1, on: "bg-bad", text: "lex.react.dir.down" },
  { dir: 0, on: "bg-muted", text: "lex.react.dir.neutral" },
  { dir: 1, on: "bg-good", text: "lex.react.dir.up" },
] as const;

/**
 * What the rulebooks expect of the market after the case: a three-part scale (bearish · neutral · bullish) with the
 * expected direction lit — both ends when it can go either way — and the kind of reaction (reversal, continuation …).
 */
function Reaction({ e }: { e: LexiconEntry }) {
  const { t } = useI18n();
  const r = e.reaction;
  if (!r) return null;
  const lit = (dir: number) => r.dir === dir || (r.dir === "both" && dir !== 0);
  const direction = r.dir === "both" ? "lex.react.dir.both" : SCALE.find((s) => s.dir === r.dir)!.text;
  return (
    <div className="flex flex-col gap-1 rounded-lg bg-surface-2 px-2.5 py-2" aria-label={`${t("lex.react.title")}: ${t(`lex.react.kind.${r.kind}`)}, ${t(direction)}`}>
      <div className="flex items-baseline justify-between gap-2 text-[11px]">
        <span className="text-muted">{t("lex.react.title")}</span>
        <span className="font-semibold text-ink">{t(`lex.react.kind.${r.kind}`)} · {t(direction)}</span>
      </div>
      <div className="grid grid-cols-3 gap-1" aria-hidden>
        {SCALE.map((s) => (
          <div key={s.dir} className="flex flex-col items-center gap-0.5">
            <span className={clsx("h-1.5 w-full rounded-full", lit(s.dir) ? s.on : "bg-line")} />
            <span className={clsx("text-[10px]", lit(s.dir) ? "text-ink" : "text-muted")}>{t(s.text)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** One line of the rule: from when the case counts (green) or what makes it invalid (red). */
function Rule({ tone, label, text }: { tone: "good" | "bad"; label: string; text: string }) {
  return (
    <p className="text-[12px] text-ink">
      <b className={tone === "good" ? "text-good-ink" : "text-bad-ink"}>{label}</b> {text}
    </p>
  );
}

/** How to read it: its points, each with its title and an example. */
function ReadList({ e }: { e: LexiconEntry }) {
  const { t } = useI18n();
  if (!e.read) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold text-ink-2">{t("lex.read")}</span>
      <ul className="flex list-disc flex-col gap-1 pl-4 text-[12px] text-ink">
        {Array.from({ length: e.read }, (_, k) => (
          <li key={k}>
            <b>{t(`lex.${e.id}.read.${k}.title`)} </b>
            {t(`lex.${e.id}.read.${k}`)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The entry's list: plain, numbered (the confirmation ladder, each step with its title) or a checklist. */
function EntryList({ e }: { e: LexiconEntry }) {
  const { t } = useI18n();
  if (!e.list) return null;
  const { n, kind, titled } = e.list;
  const items = Array.from({ length: n }, (_, k) => (
    <li key={k} className={clsx(kind === "checks" && "flex items-start gap-1.5")}>
      {kind === "checks" && <Icon name="check" className="mt-0.5 h-3.5 w-3.5 text-good-ink" />}
      <span>
        {titled && <b>{t(`lex.${e.id}.list.${k}.title`)} </b>}
        {t(`lex.${e.id}.list.${k}`)}
      </span>
    </li>
  ));
  const cls = "flex flex-col gap-0.5 pl-4 text-[12px] text-ink";
  if (kind === "ordered") return <ol className={clsx(cls, "list-decimal")}>{items}</ol>;
  return <ul className={kind === "checks" ? "flex flex-col gap-0.5 text-[12px] text-ink" : clsx(cls, "list-disc")}>{items}</ul>;
}
